import { createContext, useContext, useEffect, useMemo, useState } from "react";

const Ctx = createContext({});

/**
 * Full i18n translations for OPIKJEWELS.
 * Languages: EN (default), ID (Bahasa), CN (Simplified Chinese), TH (Thai), AR (Arabic - RTL).
 *
 * All static UI strings live here. Dynamic database fields (gemstone names, descriptions,
 * treatments, certifications) are translated on the backend via Claude + MongoDB cache —
 * the frontend simply forwards `?lang=` on every API GET request via lib/api.js.
 */
export const LANGS = {
  EN: {
    code: "EN", url: "en", label: "English", flag: "🇬🇧", dir: "ltr", htmlLang: "en",
    nav_collection: "Collection", nav_auctions: "Auction House", nav_investment: "Investment",
    nav_verify: "Verify", nav_private: "Private Sales", nav_concierge: "Concierge", nav_heritage: "Heritage",
    nav_fys: "Find Your Stone", nav_signin: "Sign In", nav_signout: "Sign Out",
    nav_admin: "Admin", nav_vip: "VIP",
    home_eyebrow: "International Gemstone Authority · Curated from Jakarta",
    home_title_1: "The Rarest Stones", home_title_2: "On Earth.",
    home_lede: "Private dealer and auction house for diamonds, sapphires, rubies, emeralds, and singular collector pieces of historic legacy.",
    cta_enter_auction: "Enter Auction", cta_view_collection: "View Collection",
    cta_buy_secure: "Buy Securely", cta_bank_transfer: "Bank Transfer",
    cta_whatsapp: "WhatsApp Concierge", cta_negotiate: "Negotiate Reserve",
    cta_place_bid: "Place Bid", cta_verify: "Verify",
    cta_request_viewing: "Request Viewing", cta_send: "Send",
    cta_download_cert: "Download Certificate",
    label_current_bid: "Current Bid", label_reserve: "Reserve",
    label_carat: "Carat", label_weight: "Weight", label_dimensions: "Dimensions",
    label_origin: "Origin", label_treatment: "Treatment",
    label_color: "Color", label_clarity: "Clarity", label_cut: "Cut",
    label_certification: "Certification", label_owner: "Owner",
    label_warranty: "Warranty", label_lab: "Issuing Lab",
    sec_signature: "Signature Pieces", sec_heritage: "Heritage",
    sec_verify: "Certificate & Warranty Portal",
    status_live: "Live", status_upcoming: "Upcoming", status_ended: "Ended",
  },
  ID: {
    code: "ID", url: "id", label: "Bahasa Indonesia", flag: "🇮🇩", dir: "ltr", htmlLang: "id",
    nav_collection: "Koleksi", nav_auctions: "Rumah Lelang", nav_investment: "Investasi",
    nav_verify: "Verifikasi", nav_private: "Penjualan Pribadi", nav_concierge: "Konsierje", nav_heritage: "Warisan",
    nav_fys: "Temukan Batu Anda", nav_signin: "Masuk", nav_signout: "Keluar",
    nav_admin: "Admin", nav_vip: "VIP",
    home_eyebrow: "Otoritas Permata Internasional · Dikurasi dari Jakarta",
    home_title_1: "Batu Termulia", home_title_2: "di Bumi.",
    home_lede: "Rumah lelang dan dealer pribadi untuk berlian, safir, rubi, zamrud, dan harta unik bersejarah.",
    cta_enter_auction: "Masuk Lelang", cta_view_collection: "Lihat Koleksi",
    cta_buy_secure: "Beli dengan Aman", cta_bank_transfer: "Transfer Bank",
    cta_whatsapp: "Konsierje WhatsApp", cta_negotiate: "Negosiasi Reserve",
    cta_place_bid: "Tempatkan Tawaran", cta_verify: "Verifikasi",
    cta_request_viewing: "Minta Penilaian", cta_send: "Kirim",
    cta_download_cert: "Unduh Sertifikat",
    label_current_bid: "Tawaran Saat Ini", label_reserve: "Reserve",
    label_carat: "Karat", label_weight: "Berat", label_dimensions: "Dimensi",
    label_origin: "Asal", label_treatment: "Perlakuan",
    label_color: "Warna", label_clarity: "Kejernihan", label_cut: "Potongan",
    label_certification: "Sertifikasi", label_owner: "Pemilik",
    label_warranty: "Garansi", label_lab: "Lab Penerbit",
    sec_signature: "Koleksi Pilihan", sec_heritage: "Warisan",
    sec_verify: "Portal Sertifikat & Garansi",
    status_live: "Berlangsung", status_upcoming: "Akan Datang", status_ended: "Selesai",
  },
  CN: {
    code: "CN", url: "cn", label: "简体中文", flag: "🇨🇳", dir: "ltr", htmlLang: "zh-CN",
    nav_collection: "珍藏", nav_auctions: "拍卖行", nav_investment: "投资宝石",
    nav_verify: "验证", nav_private: "私人销售", nav_concierge: "礼宾", nav_heritage: "传承",
    nav_fys: "寻您之石", nav_signin: "登录", nav_signout: "退出",
    nav_admin: "管理", nav_vip: "贵宾",
    home_eyebrow: "国际宝石权威 · 雅加达臻藏",
    home_title_1: "世间最稀有的", home_title_2: "宝石。",
    home_lede: "钻石、蓝宝石、红宝石、祖母绿及历史传承珍品的私人交易与拍卖行。",
    cta_enter_auction: "进入拍卖", cta_view_collection: "查看珍藏",
    cta_buy_secure: "安全购买", cta_bank_transfer: "银行转账",
    cta_whatsapp: "WhatsApp 礼宾", cta_negotiate: "议价",
    cta_place_bid: "出价", cta_verify: "验证",
    cta_request_viewing: "申请观赏", cta_send: "发送",
    cta_download_cert: "下载证书",
    label_current_bid: "当前出价", label_reserve: "保留价",
    label_carat: "克拉", label_weight: "重量", label_dimensions: "尺寸",
    label_origin: "产地", label_treatment: "处理",
    label_color: "颜色", label_clarity: "净度", label_cut: "切工",
    label_certification: "认证", label_owner: "拥有者",
    label_warranty: "保修", label_lab: "出证实验室",
    sec_signature: "臻品系列", sec_heritage: "传承",
    sec_verify: "证书与保修门户",
    status_live: "进行中", status_upcoming: "即将开始", status_ended: "已结束",
  },
  TH: {
    code: "TH", url: "th", label: "ภาษาไทย", flag: "🇹🇭", dir: "ltr", htmlLang: "th",
    nav_collection: "คอลเล็กชัน", nav_auctions: "บ้านประมูล", nav_investment: "อัญมณีเพื่อการลงทุน",
    nav_verify: "ตรวจสอบ", nav_private: "การขายส่วนตัว", nav_concierge: "คอนเซียร์จ", nav_heritage: "มรดก",
    nav_fys: "ค้นหาอัญมณีของคุณ", nav_signin: "เข้าสู่ระบบ", nav_signout: "ออกจากระบบ",
    nav_admin: "ผู้ดูแล", nav_vip: "วีไอพี",
    home_eyebrow: "ผู้เชี่ยวชาญอัญมณีระดับสากล · คัดสรรจากจาการ์ตา",
    home_title_1: "อัญมณีที่หาได้ยากที่สุด", home_title_2: "บนโลก",
    home_lede: "ผู้แทนจำหน่ายส่วนตัวและบ้านประมูลสำหรับเพชร แซฟไฟร์ ทับทิม มรกต และอัญมณีหายากทรงคุณค่า",
    cta_enter_auction: "เข้าสู่การประมูล", cta_view_collection: "ดูคอลเล็กชัน",
    cta_buy_secure: "ซื้ออย่างปลอดภัย", cta_bank_transfer: "โอนผ่านธนาคาร",
    cta_whatsapp: "คอนเซียร์จ WhatsApp", cta_negotiate: "ต่อรองราคา",
    cta_place_bid: "เสนอราคา", cta_verify: "ตรวจสอบ",
    cta_request_viewing: "ขอชมตัวอย่าง", cta_send: "ส่ง",
    cta_download_cert: "ดาวน์โหลดใบรับรอง",
    label_current_bid: "ราคาเสนอปัจจุบัน", label_reserve: "ราคาขั้นต่ำ",
    label_carat: "กะรัต", label_weight: "น้ำหนัก", label_dimensions: "ขนาด",
    label_origin: "แหล่งกำเนิด", label_treatment: "การปรับปรุง",
    label_color: "สี", label_clarity: "ความใส", label_cut: "การเจียระไน",
    label_certification: "การรับรอง", label_owner: "เจ้าของ",
    label_warranty: "การรับประกัน", label_lab: "ห้องปฏิบัติการที่ออก",
    sec_signature: "ชิ้นเอกประจำคอลเล็กชัน", sec_heritage: "มรดก",
    sec_verify: "พอร์ทัลใบรับรองและการรับประกัน",
    status_live: "กำลังประมูล", status_upcoming: "กำลังจะเริ่ม", status_ended: "สิ้นสุดแล้ว",
  },
  AR: {
    code: "AR", url: "ar", label: "العربية", flag: "🇸🇦", dir: "rtl", htmlLang: "ar",
    nav_collection: "المجموعة", nav_auctions: "دار المزادات", nav_investment: "أحجار الاستثمار",
    nav_verify: "التحقق", nav_private: "المبيعات الخاصة", nav_concierge: "الكونسيرج", nav_heritage: "التراث",
    nav_fys: "ابحث عن حجرك", nav_signin: "تسجيل الدخول", nav_signout: "تسجيل الخروج",
    nav_admin: "الإدارة", nav_vip: "كبار الزبائن",
    home_eyebrow: "السلطة الدولية للأحجار الكريمة · جاكرتا",
    home_title_1: "أندر الأحجار", home_title_2: "على الأرض.",
    home_lede: "دار مزادات وتاجر خاص للألماس والياقوت والروبي والزمرد والكنوز الفريدة.",
    cta_enter_auction: "ادخل المزاد", cta_view_collection: "عرض المجموعة",
    cta_buy_secure: "شراء آمن", cta_bank_transfer: "الدفع بحوالة بنكية",
    cta_whatsapp: "كونسيرج واتساب", cta_negotiate: "التفاوض",
    cta_place_bid: "تقديم عرض", cta_verify: "تحقق",
    cta_request_viewing: "طلب معاينة", cta_send: "إرسال",
    cta_download_cert: "تحميل الشهادة",
    label_current_bid: "العرض الحالي", label_reserve: "الحد الأدنى",
    label_carat: "قيراط", label_weight: "الوزن", label_dimensions: "الأبعاد",
    label_origin: "المنشأ", label_treatment: "المعالجة",
    label_color: "اللون", label_clarity: "النقاء", label_cut: "القطع",
    label_certification: "الشهادة", label_owner: "المالك",
    label_warranty: "الضمان", label_lab: "المختبر المُصدر",
    sec_signature: "المجموعات المميزة", sec_heritage: "التراث",
    sec_verify: "بوابة الشهادات والضمان",
    status_live: "مباشر", status_upcoming: "قادم", status_ended: "منتهي",
  },
};

const STORAGE_KEY = "opik_lang";
// Tracks whether the user has explicitly chosen a language at least once.
// Used to decide if the geo-IP suggestion + first-visit welcome popup applies.
const PREF_KEY = "opik_locale_pref_set";

// Browser → site lang mapping (used on first visit)
const BROWSER_LANG_MAP = {
  en: "EN", id: "ID", zh: "CN", "zh-cn": "CN", "zh-tw": "CN", "zh-hk": "CN",
  th: "TH", ar: "AR",
};

// Backend-locale-string ("id", "en", "cn", "th", "ar") → internal context code.
export const BACKEND_LANG_TO_CODE = {
  en: "EN", id: "ID", cn: "CN", th: "TH", ar: "AR",
};

const detectBrowserLang = () => {
  if (typeof navigator === "undefined") return "EN";
  const candidates = [navigator.language, ...(navigator.languages || [])].filter(Boolean);
  for (const raw of candidates) {
    const lower = raw.toLowerCase();
    if (BROWSER_LANG_MAP[lower]) return BROWSER_LANG_MAP[lower];
    const short = lower.split("-")[0];
    if (BROWSER_LANG_MAP[short]) return BROWSER_LANG_MAP[short];
  }
  return "EN";
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "EN";
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && LANGS[saved]) return saved;
    return detectBrowserLang();
  });
  // True once the user has confirmed a preference (welcome popup, switcher, or saved cookie).
  const [prefSet, setPrefSet] = useState(() => {
    if (typeof window === "undefined") return true;
    return localStorage.getItem(PREF_KEY) === "1";
  });

  // Persist + sync to document attributes
  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem(STORAGE_KEY, lang);
    const def = LANGS[lang] || LANGS.EN;
    document.documentElement.setAttribute("lang", def.htmlLang);
    document.documentElement.setAttribute("dir", def.dir);
  }, [lang]);

  // Explicit user action — also marks the preference as "set" so the welcome
  // popup never reappears.
  const change = (next, opts = {}) => {
    if (!LANGS[next]) return;
    setLang(next);
    if (opts.fromUser !== false) {
      try { localStorage.setItem(PREF_KEY, "1"); } catch (e) { /* ignore */ }
      setPrefSet(true);
    }
  };

  // Used by the geo-IP / welcome flow to mark "user has acknowledged" without
  // changing the locale.
  const markPrefSet = () => {
    try { localStorage.setItem(PREF_KEY, "1"); } catch (e) { /* ignore */ }
    setPrefSet(true);
  };

  // Lowercase code used for backend API translation cache keys
  const apiLang = (LANGS[lang]?.htmlLang || "en").toLowerCase();
  // Bare 2-letter code for hreflang ("en", "id", "zh", "th", "ar")
  const isoLang = apiLang === "zh-cn" ? "zh" : apiLang;

  const t = useMemo(() => {
    return (key) => {
      const def = LANGS[lang] || LANGS.EN;
      return def[key] || LANGS.EN[key] || key;
    };
  }, [lang]);

  const value = useMemo(() => ({
    lang,
    change,
    markPrefSet,
    prefSet,
    t,
    supported: Object.values(LANGS).map((l) => ({ value: l.code, label: l.label, flag: l.flag })),
    apiLang,
    isoLang,
    meta: LANGS[lang] || LANGS.EN,
  }), [lang, t, apiLang, isoLang, prefSet]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useLang = () => useContext(Ctx);
